import React, { useMemo } from "react";
import CreatableSelect from "react-select/creatable";

const PRESET_SIZES = [
  8, 10, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 48, 56, 64, 72,
];

const MAX_FONT_SIZE = 500;

function formatSizeLabel(n) {
  if (n == null || Number.isNaN(n)) return "";
  const r = Math.round(n * 100) / 100;
  return Number.isInteger(r) ? String(r) : String(r);
}

function parseFontSizeInput(input) {
  const n = parseFloat(String(input).trim().replace(",", "."));
  if (!Number.isFinite(n) || n <= 0 || n > MAX_FONT_SIZE) return null;
  return Math.round(n * 100) / 100;
}

function parseNumericInputLoose(input) {
  const n = parseFloat(String(input).trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const FontSizeSelect = ({ selectedSize, onFontSizeChange, disabled }) => {
  const options = useMemo(() => {
    const values = new Set(PRESET_SIZES);
    if (typeof selectedSize === "number" && !Number.isNaN(selectedSize)) {
      values.add(selectedSize);
    }
    return [...values]
      .sort((a, b) => a - b)
      .map((value) => ({
        value,
        label: formatSizeLabel(value),
      }));
  }, [selectedSize]);

  const value = useMemo(() => {
    if (typeof selectedSize !== "number" || Number.isNaN(selectedSize)) {
      return null;
    }
    const match = options.find(
      (o) => Math.abs(o.value - selectedSize) < 0.01
    );
    if (match) return match;
    return { value: selectedSize, label: formatSizeLabel(selectedSize) };
  }, [selectedSize, options]);

  return (
    <CreatableSelect
      value={value}
      onChange={(selectedOption) => {
        if (!selectedOption) return;
        const parsed =
          typeof selectedOption.value === "number"
            ? selectedOption.value
            : parseFontSizeInput(selectedOption.value);
        if (parsed != null) onFontSizeChange(parsed);
      }}
      options={options}
      placeholder="Size"
      isDisabled={disabled}
      isClearable={false}
      formatCreateLabel={(inputValue) => `Use size ${inputValue.trim()}`}
      isValidNewOption={(inputValue) => parseFontSizeInput(inputValue) != null}
      noOptionsMessage={({ inputValue }) => {
        const n = parseNumericInputLoose(inputValue);
        if (n != null && n > MAX_FONT_SIZE) {
          return `Max font size is ${MAX_FONT_SIZE}`;
        }
        return "No options";
      }}
      className="font-size-select"
    />
  );
};

export default FontSizeSelect;
